import type { DataRoomNode } from "@vega-api/generated/types/dataRoomNode";
import type { VegaDataRoomNodes } from "@vega-api/generated/types/vegaDataRoomNodes";
import { m } from "@vega-core/paraglide/messages";
import { isViewableInApp } from "@vega-modules/scenes/CoInvestments/utils/dataRoomUtils";

import { ROOT_FOLDER_VALUE } from "../constants";
import type {
  AccessMap,
  DownloadableMap,
  NodeMap,
  NodeType,
  ParsedDataRoom,
  PermissionChange,
  TreeNode,
  TriState,
} from "../types";

export function compareNodes(a: TreeNode, b: TreeNode): number {
  if (a.type !== b.type) return a.type === "folder" ? -1 : 1;

  return a.name.localeCompare(b.name);
}

export function parseApiResponse(response: VegaDataRoomNodes): ParsedDataRoom {
  const nodeMap: NodeMap = {};
  const access: AccessMap = {};
  const downloadable: DownloadableMap = {};
  const allDealOpportunityIds = response.dealOpportunities.map(
    (dealOpportunity) => dealOpportunity.id,
  );

  response.nodes.forEach((n) => {
    if (!n.isFolder) {
      nodeMap[n.id] = {
        id: n.id,
        name: n.name,
        type: "file",
        parentId: n.parentId ?? null,
        children: [],
        createdAt: n.createdAt ?? null,
        file: n.file,
      };

      const fileAccess: Record<string, boolean> = {};
      access[n.id] = fileAccess;
      allDealOpportunityIds.forEach((dealOpportunityId) => {
        fileAccess[dealOpportunityId] = false;
      });
      (n.dealOpportunityPermissions ?? []).forEach((perm) => {
        fileAccess[perm.dealOpportunityId] = true;
      });
      downloadable[n.id] = isViewableInApp(nodeMap[n.id]!)
        ? (n.isDownloadable ?? false)
        : true;
    } else {
      nodeMap[n.id] = {
        id: n.id,
        name: n.name,
        type: "folder",
        parentId: n.parentId ?? null,
        children: [],
        createdAt: null,
      };
    }
  });

  // Build folder children from parentId
  Object.values(nodeMap).forEach((node) => {
    if (node?.parentId) {
      const parent = nodeMap[node.parentId];

      if (parent?.type === "folder") parent.children.push(node.id);
    }
  });

  Object.values(nodeMap)
    .filter((n): n is TreeNode => n?.type === "folder")
    .forEach((folder) => {
      folder.children.sort((a, b) => {
        const nodeA = nodeMap[a];
        const nodeB = nodeMap[b];

        if (!nodeA || !nodeB) return 0;

        return compareNodes(nodeA, nodeB);
      });
    });

  // Compute paths for nodes
  for (const node of Object.values(nodeMap)) {
    if (node && !node.path) {
      node.path = getNodePath(node.id, nodeMap);
    }
  }

  return { nodeMap, access, downloadable };
}

export function getRootIds(nodeMap: NodeMap): string[] {
  return Object.values(nodeMap)
    .filter((n): n is TreeNode => n !== undefined && !n.parentId)
    .sort(compareNodes)
    .map((n) => n.id);
}

export function getDepth(id: string, nodeMap: NodeMap): number {
  let depth = 0;
  let current = nodeMap[id];
  while (current?.parentId) {
    depth++;
    current = nodeMap[current.parentId];
  }

  return depth;
}

export function getNodePath(id: string, nodeMap: NodeMap): string {
  const parts: string[] = [];
  let current = nodeMap[id];

  while (current) {
    parts.unshift(current.name);
    current = current.parentId ? nodeMap[current.parentId] : undefined;
  }

  return `/${parts.join("/")}`;
}

export function getDescendants(id: string, nodeMap: NodeMap): string[] {
  const result: string[] = [];

  function walk(nodeId: string) {
    const node = nodeMap[nodeId];

    if (!node) return;

    result.push(nodeId);
    node.children.forEach(walk);
  }

  walk(id);

  return result;
}

export function computeDiff(
  savedAccess: AccessMap,
  currentAccess: AccessMap,
  savedDownloadable: DownloadableMap,
  currentDownloadable: DownloadableMap,
  nodeMap: NodeMap,
): PermissionChange[] {
  const changes: PermissionChange[] = [];
  const fileIds = new Set([
    ...Object.keys(savedAccess),
    ...Object.keys(currentAccess),
  ]);

  fileIds.forEach((fileId) => {
    const saved = savedAccess[fileId] ?? {};
    const current = currentAccess[fileId] ?? {};
    const dealOpportunityIds = new Set([
      ...Object.keys(saved),
      ...Object.keys(current),
    ]);

    dealOpportunityIds.forEach((dealOpportunityId) => {
      const fromVal = !!saved[dealOpportunityId];
      const toVal = !!current[dealOpportunityId];

      if (fromVal !== toVal) {
        changes.push({
          type: "access",
          fileId,
          dealOpportunityId,
          from: fromVal,
          to: toVal,
          fileName: nodeMap[fileId]?.name ?? "",
        });
      }
    });

    const savedDl = !!savedDownloadable[fileId];
    const currentDl = !!currentDownloadable[fileId];

    if (savedDl !== currentDl) {
      changes.push({
        type: "download",
        fileId,
        from: savedDl,
        to: currentDl,
        fileName: nodeMap[fileId]?.name ?? "",
      });
    }
  });

  return changes;
}

export function getSiblingNames(
  parentId: string | null,
  type: NodeType,
  nodeMap: NodeMap,
): Set<string> {
  const children =
    parentId === null
      ? Object.values(nodeMap).filter(
          (n): n is TreeNode => n !== undefined && n.parentId === null,
        )
      : (nodeMap[parentId]?.children ?? [])
          .map((id) => nodeMap[id])
          .filter((n): n is TreeNode => n !== undefined);

  return new Set(children.filter((n) => n.type === type).map((n) => n.name));
}

export function triState(vals: boolean[]): TriState {
  if (vals.length === 0) return false;
  const count = vals.filter(Boolean).length;

  if (count === 0) return false;

  if (count === vals.length) return true;

  return "indeterminate";
}

export type FolderOption = {
  id: string | null;
  label: string;
  depth: number;
  path: string;
};

export type AddedNodeSummary = {
  id: string;
  isRoot: boolean;
  locationPath: string;
  type: "file" | "folder";
  count: number;
};

export function getFolderDisplayPath(nodeId: string, nodeMap: NodeMap): string {
  const node = nodeMap[nodeId];

  if (!node) return "";

  if (!node.parentId) return node.name;

  const parentPath = getFolderDisplayPath(node.parentId, nodeMap);

  return parentPath
    ? m.coInvestments_dataRoom_folderPathJoin({
        parentPath,
        folderName: node.name,
      })
    : node.name;
}

export function buildAddedNodeSummaries(nodeMap: NodeMap): AddedNodeSummary[] {
  const groupedNodes = new Map<string, AddedNodeSummary>();

  for (const node of Object.values(nodeMap)) {
    if (!node?.isNew) continue;

    const summaryKey = `${node.parentId ?? "root"}-${node.type}`;
    const existingSummary = groupedNodes.get(summaryKey);

    if (existingSummary) {
      existingSummary.count += 1;
      continue;
    }

    groupedNodes.set(summaryKey, {
      id: summaryKey,
      isRoot: node.parentId === null,
      locationPath:
        node.parentId === null
          ? ""
          : getFolderDisplayPath(node.parentId, nodeMap),
      type: node.type,
      count: 1,
    });
  }

  return [...groupedNodes.values()].toSorted((summaryA, summaryB) => {
    if (summaryA.isRoot !== summaryB.isRoot) {
      return summaryA.isRoot ? -1 : 1;
    }

    return (
      summaryA.locationPath.localeCompare(summaryB.locationPath) ||
      summaryA.type.localeCompare(summaryB.type)
    );
  });
}

export function buildFolderOptions(nodeMap: NodeMap): FolderOption[] {
  const options: FolderOption[] = [
    { id: null, label: m.coInvestments_dataRoom_root(), depth: 0, path: "" },
  ];

  function walk(nodeId: string, depth: number) {
    const node = nodeMap[nodeId];

    if (!node || node.type !== "folder") return;

    options.push({
      id: nodeId,
      label: node.name,
      depth,
      path: getFolderDisplayPath(nodeId, nodeMap),
    });
    node.children.forEach((childId) => walk(childId, depth + 1));
  }

  getRootIds(nodeMap).forEach((id) => walk(id, 1));

  return options;
}

export type FolderDropdownData = {
  depthMap: Map<string, number>;
  options: { value: string; label: string }[];
  pathMap: Map<string, string>;
};

export function buildFolderDropdownData(nodeMap: NodeMap): FolderDropdownData {
  const folderOptions = buildFolderOptions(nodeMap);
  const depthMap = new Map<string, number>();
  const options: { value: string; label: string }[] = [];
  const pathMap = new Map<string, string>();

  for (const opt of folderOptions) {
    const key = opt.id ?? ROOT_FOLDER_VALUE;
    depthMap.set(key, opt.depth);
    options.push({ value: key, label: opt.label });
    pathMap.set(key, opt.path);
  }

  return { depthMap, options, pathMap };
}

export function serializeNodes(
  nodeMap: NodeMap,
  access: AccessMap,
  downloadable: DownloadableMap,
  excludeIds?: Set<string>,
): DataRoomNode[] {
  return Object.values(nodeMap)
    .filter((n): n is TreeNode => n !== undefined && !excludeIds?.has(n.id))
    .map((node): DataRoomNode => {
      if (node.type === "file") {
        const fileAccess = access[node.id] ?? {};

        return {
          id: node.id,
          name: node.name,
          isFolder: false,
          parentId: node.parentId ?? undefined,
          isDownloadable: !!downloadable[node.id],
          createdAt: node.createdAt ?? undefined,
          file: node.file,
          dealOpportunityPermissions: Object.entries(fileAccess)
            .filter(([, hasAccess]) => hasAccess)
            .map(([dealOpportunityId]) => ({ dealOpportunityId })),
        };
      }

      return {
        id: node.id,
        name: node.name,
        isFolder: true,
        parentId: node.parentId ?? undefined,
      };
    });
}
